import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchItems,
    createItem,
    updateItem,
    deleteItem,
    setPagination,
    setSort,
    searchItems,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination,
} from '../../store/modules/item/itemActions';
import ItemForm from '../../components/pageModule/Items/ItemForm';
import { useHasPermission } from '../../utils/permissionUtils';

const MySwal = withReactContent(Swal);

const Items = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = [],
    isMultipleSelection = false
}) => {

    const hasCreatePermission = useHasPermission(['create items']);
    const hasUpdatePermission = useHasPermission(['update items']);
    const hasViewPermission = useHasPermission(['view items']);

    const itemState = useSelector(state => state.item);
    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createItem(formData));
            toast.success('Item created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating item', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateItem(id, formData));
            toast.success('Item updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating item', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteItem(id));
                toast.success('Item deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting item', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const columns = [
        {
            name: 'Item Code',
            selector: row => row.item_code,
        },
        {
            name: 'Name',
            selector: row => row.name?.en ?? '-',
            sortable: true,
            sortField: 'items.name',
        },
        {
            name: 'Category',
            selector: row => row.category?.label ?? '-',
        },
        {
            name: 'Item Type',
            selector: row => row.item_type?.label ?? '-',
        },
        {
            name: 'VAT Group',
            selector: row => row.vat_group?.label ?? '-',
        },
        {
            name: 'Unit Price',
            selector: row => row.unit_price,
        },
        {
            name: 'Available Qty',
            selector: row => {
                const qty = parseFloat(row.available_stock) || 0;
                return `${qty.toFixed(3)} ${row.measurement_unit?.label ?? ''}`;
            },
            sortable: true,
            sortField: 'available_stock',
        },
        {
            name: 'Unit Cost',
            selector: row => row.unit_cost,
        },
        {
            name: 'Status',
            selector: row => row.is_active ?
                <span className="badge bg-success">Active</span> :
                <span className="badge bg-danger">Inactive</span>,
        },
    ];

    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchItems(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchItems(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
    };

    let defaultAvailableActions = ['search'];
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission || hasUpdatePermission) {
        defaultAvailableActions = ['actions', 'edit', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Items"
                moduleName="Item"
                moduleKey="items"
                state={itemState}
                actions={actions}
                sidebarClassName="right-sidebar-80"
                FormComponent={ItemForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                selectableRows={isMultipleSelection}
                onSelectedRowsChange={onSelectedRowsChange}
                onClickedRow={onClickedRow}
                selectedIds={selectedIds}
                externalCondition={externalCondition}
            />
        </>
    );
};

export default Items; 